import React, { useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { toast } from "react-toastify";
import { postRequest } from '../../../utils/api';
import { logout } from '../../../utils/auth';

interface AddPartnerModalProps {
  show: boolean;
  handleClose: () => void;
}

const AddPartnerModal: React.FC<AddPartnerModalProps> = ({ show, handleClose }) => {
  const [activeTab, setActiveTab] = useState(1);
  const [loading, setLoading] = useState(false);

  const initialFormState = {
    username: '',
    password: '',
    companyName: '',
    contactPerson: '',
    email: '',
    mobile: '',
    industry: ''
  };
  
  const [formData, setFormData] = useState(initialFormState);

  const resetForm = () => {
    setFormData(initialFormState);
    setActiveTab(1);
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleNext = () => {
    setActiveTab((prev) => prev + 1);
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      
      const response = await postRequest('/white-label/create-partner', formData); 

      if (response.status === 'success') {
        toast.success(response.message || 'Partner created successfully!');
        resetForm();
        handleClose(); 
      } else if (response.status === 'session_expired') {
        logout();
      } else {
        toast.error(response.message || 'Something went wrong');
      }
    } catch (error: any) {
      console.error('Error saving partner:', error);
      toast.error(error.message || 'An error occurred while connecting to the server.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal className='s_modal' show={show} onHide={() => { handleClose(); resetForm(); }} backdrop="static">
      <div className='modal-header'>
        <div className="titles">
          <div className="icon-badge bg-blue-light-4">
            <svg width="19" height="19" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
              <circle cx="9" cy="7" r="4"></circle>
              <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
              <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
            </svg>
          </div>
          <div>
            <h2>Add Partner</h2>
            <p>Onboard a new white-label partner and configure their account settings.</p>
          </div>
        </div>
        <button className="close-btn" aria-label="Close" onClick={() => { handleClose(); resetForm(); }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"></path></svg>
        </button>
      </div>

      <div className='tabs'>
        <button className={`tab ${activeTab === 1 ? 'active' : ''}`} onClick={() => setActiveTab(1)}> Login & Account</button>
        <button className={`tab ${activeTab === 2 ? 'active' : ''}`} onClick={() => setActiveTab(2)}> Business Details</button>
        <button className={`tab ${activeTab === 3 ? 'active' : ''}`} onClick={() => setActiveTab(3)}>Address Details</button>
        <button className={`tab ${activeTab === 4 ? 'active' : ''}`} onClick={() => setActiveTab(4)}>White-label / Branding</button>
        <button className={`tab ${activeTab === 5 ? 'active' : ''}`} onClick={() => setActiveTab(5)}>Contact Details</button>
      </div>

      <div className='modal-body'>
        <div className='body active'>

          {/* TAB 1: Login & Account */}
          {activeTab === 1 && (
            <div className='row g-3'>
              <div className='col-lg-6'>
                <div className='form-group mb-0'>
                  <label>Username / Email <span className='text-red'>*</span></label>
                  <input 
                    type="text" 
                    className="form-control" 
                    name="username" 
                    value={formData.username}
                    onChange={handleChange}
                  />
                </div>
              </div>
              <div className='col-lg-6'>
                <div className='form-group mb-0'>
                  <label>Password <span className='text-red'>*</span></label>
                  <input 
                    type="password" 
                    className="form-control" 
                    name="password" 
                    value={formData.password}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>
          )}

          {/* TAB 2: Business Details */}
          {activeTab === 2 && (
            <div className='row g-3'>
              <div className='col-lg-6'>
                <div className='form-group mb-0'>
                  <label>Company Name <span className='text-red'>*</span></label>
                  <input 
                    type="text" 
                    className="form-control" 
                    name="companyName"
                    value={formData.companyName}
                    onChange={handleChange} 
                  />
                </div>
              </div>
              <div className='col-lg-6'>
                <div className='form-group mb-0'>
                  <label>Contact Person Name <span className='text-red'>*</span></label>
                  <input 
                    type="text" 
                    className="form-control" 
                    name="contactPerson"
                    value={formData.contactPerson}
                    onChange={handleChange} 
                  />
                </div>
              </div>
              <div className='col-lg-6'>
                <div className='form-group mb-0'>
                  <label>Email <span className='text-red'>*</span></label>
                  <input 
                    type="email" 
                    className="form-control" 
                    name="email"
                    value={formData.email}
                    onChange={handleChange} 
                  />
                </div>
              </div>
              <div className='col-lg-6'>
                <div className='form-group mb-0'>
                  <label>Mobile no. <span className='text-red'>*</span></label>
                  <input 
                    type="text" 
                    className="form-control" 
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange} 
                  />
                </div>
              </div>
              <div className='col-lg-12'>
                <div className='form-group mb-0'>
                  <label>Business Type <span className='text-red'>*</span></label>
                  <select
                    name="industry"
                    className="form-select"
                    id="industry"
                    value={formData.industry}
                    onChange={handleChange}
                  >
                    <option value="">Select your Industry</option>
                    <option value="Ecommerce">Ecommerce</option>
                    <option value="Education">Education</option>
                    <option value="Automotive">Automotive</option>
                    <option value="IT Services">IT Services</option>
                    <option value="Real Estate">Real Estate</option>
                    <option value="Entertainment">Entertainment</option>
                    <option value="Finance & Banking">Finance & Banking</option>
                    <option value="Medical & Health">Medical & Health</option>
                    <option value="Resturant & Hotel">Resturant & Hotel</option>
                    <option value="Beauty, Spa and salon">Beauty, Spa and salon</option>
                    <option value="Shopping & Retail">Shopping & Retail</option>
                    <option value="Travel & Transportation">Travel & Transportation</option>
                    <option value="Professional Services">Professional Services</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* TAB 3: Address Details */}
          {activeTab === 3 && (
            <div>
              {/* Address fields go here */}
            </div>
          )}

          {/* TAB 4: White-label / Branding */}
          {activeTab === 4 && (
            <div>
              {/* Branding fields go here */}
            </div>
          )}

          {/* TAB 5: Contact Details */}
          {activeTab === 5 && (
            <div>
              {/* Contact fields go here */}
            </div>
          )}

        </div>
      </div>

      <div className='modal-footer'>
        <span className="footer-note">Fields marked * are required</span>
        <div className='footer-actions'>
          <Button variant="white" onClick={() => { handleClose(); resetForm(); }} className="me-2" disabled={loading}>
            Cancel
          </Button>
          {activeTab < 5 ? (
            <Button variant="primary" onClick={handleNext}>Next</Button>
          ) : (
            <Button variant="primary" onClick={handleSave} disabled={loading}>
              {loading ? "Saving..." : "Save"}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default AddPartnerModal;